import { inject, Injectable } from '@angular/core';
import {
  AuthorizationService,
  CommonUtils,
  DataModel,
  DataModelConverter,
  HttpFilter,
  ModelProperty,
  NameValue,
  ObjectFactory,
  PropertyType,
  RestService,
  StandardEntity,
  Time,
} from '@infodata-group/inf-commons';
import {
  BehaviorSubject,
  catchError,
  forkJoin,
  map,
  Observable,
  of,
  Subject,
} from 'rxjs';
import { DateUtils } from '../tool/date-utils';
import { switchMap, tap } from 'rxjs/operators';
import { Dayjs } from 'dayjs';
import { AbsenceType } from '../model/absence-type';
import { Absence } from '../model/absence';
import { AbsenceStatusFactory } from '../factory/absence-status-factory';
import { AbsenceFactory } from '../factory/absence-factory';
import { AbsenceTypeFactory } from '../factory/absence-type-factory';
import { AbsenceInputTypeEnum } from '../enum/absence-input-type.enum';
import { DaysOff } from '../model/days-off';
import { DaysOffFactory } from '../factory/days-off-factory';
import { DtoAbsenceAction } from '../type/dto-absence-action';
import { HolidayFactory } from '../factory/holiday-factory';

@Injectable({
  providedIn: 'root',
})
export class TimeService {
  private rest = inject(RestService);
  private auth = inject(AuthorizationService);

  get pendingCount(): number {
    if (this._pendingAbsences && this._pendingAbsences.data) {
      return this._pendingAbsences.data.length;
    } else {
      return 0;
    }
  }
  public status: StandardEntity[];
  public types: AbsenceType[];
  private model: DataModel;
  private typeGenerated = false;
  private pendingObs$ = new BehaviorSubject<NameValue[]>(null);
  private _pendingAbsences: { date: Date; data: Absence[] };

  public getBalance(absenceCode: string, userId: string): Observable<string> {
    const obs = new Subject<string>();
    const httpFilter = new HttpFilter();
    httpFilter.setFilters('code', absenceCode);
    httpFilter.setFilters('user', userId);
    this.rest
      .get(new ObjectFactory('balance'), {
        httpFilter: httpFilter,
        showLoading: false,
      })
      .subscribe({
        next: (result) => {
          const balance = result;
          if (balance) {
            const time = DateUtils.convertNumberToTime(balance['value']);
            obs.next(CommonUtils.convertTimeToString(time));
          }
          obs.complete();
        },
        error: (error) => {
          obs.error(error);
          obs.complete();
        },
      });
    return obs.asObservable();
  }

  public checkDuration(
    absenceCode: any,
    start: { date: Date; time: Time },
    end: { date: Date; time: Time },
    userId: string,
    key: string,
  ): Observable<string> {
    if (!absenceCode) {
      return of('-');
    }
    const obs = new Subject<string>();
    const data = {
      codeAbsence: absenceCode.id,
      dateStart: CommonUtils.convertDateToString(start.date),
      timeStart:
        start && start.time
          ? CommonUtils.convertTimeToString(start.time)
          : null,
      dateEnd: CommonUtils.convertDateToString(end.date),
      timeEnd:
        end && end.time ? CommonUtils.convertTimeToString(end.time) : null,
      user: userId,
      id: key,
    };
    this.rest.post('duration', data, { showLoading: false }).subscribe({
      next: (result) => {
        if (result) {
          const number = parseFloat(result.duration);
          if (!isNaN(number)) {
            const time = DateUtils.convertNumberToTime(number);
            obs.next(CommonUtils.convertTimeToString(time));
          }
          obs.complete();
        }
      },
      error: (error) => obs.error(error),
    });
    return obs.asObservable();
  }

  public loadStatus(): Observable<StandardEntity[]> {
    if (this.status) {
      return of(this.status);
    } else {
      return this.rest.getAll(new AbsenceStatusFactory()).pipe(
        tap((status) => {
          this.status = status;
        }),
      );
    }
  }

  public loadAbsences(
    filters: HttpFilter,
    api: string,
    showLoader = true,
  ): Observable<Absence[]> {
    if (this.types && this.status) {
      if (!this.typeGenerated && this.model) {
        this.generateTypeFormatter();
      }
      return this.rest.getAll(
        new AbsenceFactory(this.types, this.status, api),
        {
          httpFilter: filters,
          showLoading: showLoader,
        },
      );
    } else {
      const param = new HttpFilter();
      param.setFilters('full', true);
      const group$ = this.rest.getAll(new AbsenceTypeFactory(), {
        httpFilter: param,
      });
      const status$ = this.rest.getAll(new AbsenceStatusFactory());
      return forkJoin([group$, status$]).pipe(
        switchMap((values) => {
          this.types = values[0];
          if (!this.typeGenerated && this.model) {
            this.generateTypeFormatter();
          }
          this.status = values[1];
          return this.rest.getAll(
            new AbsenceFactory(this.types, this.status, api),
            {
              httpFilter: filters,
            },
          );
        }),
      );
    }
  }

  getStatus(id: string) {
    const status = this.status?.find((st) => st.id === id);
    return status ?? new StandardEntity(id, id);
  }

  public getModel(): Observable<DataModel> {
    if (this.model) {
      return of(this.model);
    }
    return this.rest.options('absences').pipe(
      tap((model) => {
        this.model = model;
        const p = model.find('user');
        if (p) {
          const subModel = new DataModelConverter().convert(p);
          subModel.properties.forEach((sp) => {
            this.model.addProperty(sp);
          });
        }
        const absenceCode = this.model.find('absenceCode');
        absenceCode.positionByContext.form = 0;
        absenceCode.position = 0;
        absenceCode.titleByContext.table = absenceCode.title;
        // absenceCode.title = this.trl.instant('addForm.labelAbsence');
        absenceCode.formatter = {
          type: 'icon',
          field: 'absenceType',
        };
        const dateStartProp = this.model.find('dateStart');
        dateStartProp.type = PropertyType.date;
        dateStartProp.titleByContext.table = dateStartProp.title;
        this.generateExportModel('dateStart', 'dateEnd');
        this.generateStateFormatter();
      }),
    );
  }

  private generateTypeFormatter() {
    if (!this.typeGenerated) {
      this.typeGenerated = true;
      const groups = this.types;
      const absenceCode = this.model.find('absenceCode');
      const values: any = {};
      for (const gr of groups) {
        const styleSheet = document.styleSheets[2];
        const color = gr.color;
        const cssClass = `icon-group-${gr.id.replace(/\s/g, '')}`;
        values[gr.id] = 'square icon-bold ' + cssClass;
        const rule = `.${cssClass} { color: ${color}; }`;
        // Insert the rule in stylesheet
        styleSheet.insertRule(rule, styleSheet.cssRules.length);
      }

      absenceCode.formatter = {
        type: 'icon',
        field: 'absenceTypeCode',
        displayText: true,
        values: values,
      };
    }
  }

  public generateStateFormatter() {
    const propertyState = this.model.find('state');
    if (propertyState) {
      propertyState.formatter = {
        type: 'icon',
        field: 'stateId',
        displayText: true,
        values: {
          '0': 'circle icon-bold warning',
          '1': 'circle icon-bold success',
          '2': 'circle icon-bold danger',
          '3': 'circle icon-bold light',
        },
      };
    }
  }

  public changePendingCount(absences: Absence[]) {
    if (absences) {
      const nvs: NameValue[] = [];
      for (const absence of absences) {
        let user = nvs.find((nv) => nv.name === absence.user.id);
        if (!user) {
          user = new NameValue(absence.user.id, 0);
          nvs.push(user);
        }
        user.value += 1;
      }
      this._pendingAbsences = { date: new Date(), data: absences };
      this.pendingObs$.next(nvs);
    }
  }

  public subscribePendingCount(next: (pendingUsers: NameValue[]) => void) {
    this.pendingObs$.subscribe(next);
  }

  loadPendingAbsence(): Observable<Absence[]> {
    const filters = new HttpFilter([
      new NameValue('onlyRequest', '1'),
      new NameValue('level', this.auth.userContext.role),
    ]);
    return this.loadAbsences(filters, 'absences', false).pipe(
      tap((data) => {
        this.changePendingCount(data);
      }),
    );
  }

  public loadDaysOff(
    dateStart: string,
    dateEnd: string,
    operatorId?: string,
  ): Observable<DaysOff[]> {
    const factory = new DaysOffFactory(dateStart, dateEnd, operatorId);
    return this.rest.getAll(new ObjectFactory(factory.getApi())).pipe(
      map((result) => factory.build(result)),
      catchError(() => of([])),
    );
  }

  private generateExportModel(...props: string[]) {
    for (const p of props) {
      const mp = this.model.find(p);
      if (mp) {
        const id = p + 'Formatted';
        const formattedProp = new ModelProperty(
          id,
          mp.title,
          PropertyType.string,
        );
        formattedProp.existOnTable = false;
        formattedProp.showOnForm = false;
        this.model.addProperty(formattedProp);
        mp.replaceOnExport = p + 'Formatted';
      }
    }
  }

  public getStatusIcon(status: string) {
    switch (status) {
      case '0':
        return 'face-thinking';
      case '1':
        return 'thumbs-up';
      case '2':
        return 'thumbs-down';
    }
  }

  public getStatusColor(status: string) {
    switch (status) {
      case '0':
        return '#FAAA0A';
      case '1':
        return '#48CFAD';
      case '2':
        return '#99ABB2';
    }
  }

  public generateTimes(
    dateStart: Dayjs,
    dateEnd: Dayjs,
    mode: AbsenceInputTypeEnum,
    timeSlot?: StandardEntity,
  ): {
    start: { date: Date; time: Time };
    end: { date: Date; time: Time };
  } | null {
    let start, end;
    if (mode === AbsenceInputTypeEnum.details) {
      start = {
        date: dateStart.toDate(),
        time: { hours: dateStart.hour(), minutes: dateStart.minute() },
      };
      end = {
        date: dateEnd.toDate(),
        time: { hours: dateEnd.hour(), minutes: dateEnd.minute() },
      };
    } else if (mode === AbsenceInputTypeEnum.partial) {
      const split = timeSlot ? timeSlot.label.split('-') : null;
      if (split) {
        const ts = CommonUtils.convertStrToTime(split[0]);
        const te = CommonUtils.convertStrToTime(split[1]);
        start = { date: dateStart.toDate(), time: ts };
        end = { date: dateStart.toDate(), time: te };
      } else {
        return null;
      }
    } else {
      start = { date: dateStart.toDate(), time: { hours: 0, minutes: 0 } };
      end = { date: dateEnd.toDate(), time: { hours: 23, minutes: 59 } };
    }
    return { start, end };
  }

  public deleteAbsence(absence: Absence, showLoading = true) {
    return this.rest.delete('absence', absence.id, {
      showLoading: showLoading,
    });
  }

  public updateAbsence(absence: any, showLoading = true) {
    return this.rest.put('absence', absence, { showLoading: showLoading });
  }

  public getAbsenceActions(absence: Absence) {
    return this.rest.get<DtoAbsenceAction>(
      new ObjectFactory(`absence-action/${absence.id}`),
    );
  }

  public loadHolidays(year: number) {
    return this.rest.get(
      new HolidayFactory('/' + this.auth.user.name + '/' + year),
    );
  }

  public getAbsence(id: string) {
    return this.rest.getById<Absence>(new ObjectFactory('absence'), id);
  }

  public loadAbsence(api: string, id: string): Observable<Absence> {
    return this.rest.getById(
      new AbsenceFactory(this.types, this.status, api),
      id,
    );
  }
}
